import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  X,
  Download,
  Share2,
  Sparkles,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

import { fetchShareCard, generateShareCard } from "../lib/api";
import { trackFP } from "../lib/firstPartyAnalytics";

/**
 * ShareJourneyCard — "save this look" modal that downloads a single
 * 1080×1920 IG-Stories JPEG stacking every cached step preview.
 *
 * Render path (inside the inner component):
 *   1. On mount → try GET cached card. If hit, jump straight to ready.
 *   2. Else POST compose (~150ms). If 200 → ready. If 422 with
 *      `missing_step_indices` → friendly "see steps N, M first" state.
 *   3. Download button writes the JPEG via an anchor; native Web
 *      Share is offered when supported.
 *
 * Architecture note: the outer component is a thin lifecycle wrapper.
 * The inner (`Inner`) component is mounted only when `open === true`,
 * so its useState defaults reset naturally on every open — no
 * setState-in-effect required.
 *
 * Distinct from `ShareCard.jsx` — that one composites a finished-look
 * SELFIE; this one stitches the per-step AI previews into a journey.
 */

const dataUrlToBlob = (dataUrl) => {
  const [meta, b64] = dataUrl.split(",");
  const mime = meta.match(/data:(.*?);/)?.[1] || "image/jpeg";
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
};

const Inner = ({ onClose, routineId, routineGoal, t }) => {
  // States: loading (initial) | ready | missing | error
  const [state, setState] = useState("loading");
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [missingSteps, setMissingSteps] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);

  const compose = useCallback(async () => {
    if (!routineId) return;
    setState("loading");
    try {
      const data = await generateShareCard(routineId);
      if (data?.image_data_url) {
        setImageDataUrl(data.image_data_url);
        setState("ready");
        trackFP("share_card_journey_ready", {
          routine_id: routineId,
          cached: !!data.cached,
        });
      } else {
        throw new Error("empty response");
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 422 && detail?.missing_step_indices?.length) {
        setMissingSteps(detail.missing_step_indices);
        setState("missing");
        trackFP("share_card_journey_missing", {
          routine_id: routineId,
          missing_count: detail.missing_step_indices.length,
        });
        return;
      }
      setErrorMsg(detail?.message || err?.message || "Compose failed");
      setState("error");
    }
  }, [routineId]);

  // Mount: try cached first, fall back to compose. Single effect, no
  // synchronous setState anti-pattern — we only update state inside
  // the async resolution path or in the cached-hit branch.
  useEffect(() => {
    if (!routineId) return undefined;
    let cancelled = false;
    (async () => {
      trackFP("share_card_journey_opened", { routine_id: routineId });
      try {
        const cached = await fetchShareCard(routineId);
        if (cancelled) return;
        if (cached?.image_data_url) {
          setImageDataUrl(cached.image_data_url);
          setState("ready");
          trackFP("share_card_journey_ready", {
            routine_id: routineId,
            cached: true,
          });
          return;
        }
      } catch (err) {
        // 404 = no cache → fall through to compose
      }
      if (cancelled) return;
      await compose();
    })();
    return () => {
      cancelled = true;
    };
  }, [routineId, compose]);

  const handleDownload = useCallback(() => {
    if (!imageDataUrl) return;
    try {
      const blob = dataUrlToBlob(imageDataUrl);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeGoal = (routineGoal || "look")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .slice(0, 40);
      a.href = url;
      a.download = `trubeaute-${safeGoal}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
      trackFP("share_card_journey_downloaded", { routine_id: routineId });
      toast.success(t("shareJourney.toastDownloaded"));
    } catch (err) {
      toast.error(t("shareJourney.toastDownloadFailed"));
    }
  }, [imageDataUrl, routineGoal, routineId, t]);

  const handleNativeShare = useCallback(async () => {
    if (!imageDataUrl) return;
    try {
      const blob = dataUrlToBlob(imageDataUrl);
      const file = new File([blob], "trubeaute-look.jpg", {
        type: "image/jpeg",
      });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: t("shareJourney.shareTitle"),
          text: t("shareJourney.shareText"),
        });
        trackFP("share_card_journey_native_shared", { routine_id: routineId });
        return;
      }
    } catch (err) {
      // User cancelled — silent
      if (err?.name === "AbortError") return;
    }
    // Fallback to download for browsers without Web Share
    handleDownload();
  }, [imageDataUrl, routineId, handleDownload, t]);

  return (
    <motion.div
      className="bg-[#FAF7F5] rounded-3xl shadow-2xl max-w-md w-full max-h-[92vh] overflow-hidden flex flex-col"
      initial={{ scale: 0.92, y: 24 }}
      animate={{ scale: 1, y: 0 }}
      exit={{ scale: 0.92, y: 24 }}
      transition={{ type: "spring", stiffness: 280, damping: 26 }}
      onClick={(e) => e.stopPropagation()}
      data-testid="share-journey-modal"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-5 pb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center shadow-[0_3px_10px_rgba(155,106,108,0.3)]">
            <Sparkles className="h-3.5 w-3.5 text-white" />
          </div>
          <h2
            className="font-serif italic text-lg text-[#2C2123]"
            data-testid="share-journey-title"
          >
            {t("shareJourney.title")}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-[#8A7D82] hover:text-[#2C2123] transition-colors p-1 -mr-1"
          aria-label={t("shareJourney.close")}
          data-testid="share-journey-close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 pb-5">
        {state === "loading" && (
          <div
            className="py-12 text-center"
            data-testid="share-journey-loading"
          >
            <Loader2 className="h-8 w-8 animate-spin text-[#9B6A6C] mx-auto mb-4" />
            <p className="text-sm text-[#5C4B51] leading-relaxed">
              {t("shareJourney.loadingCopy")}
            </p>
          </div>
        )}

        {state === "ready" && imageDataUrl && (
          <div data-testid="share-journey-ready">
            <div className="rounded-2xl overflow-hidden bg-[#ECDAD5] shadow-[0_8px_30px_rgba(44,33,35,0.15)]">
              <img
                src={imageDataUrl}
                alt={t("shareJourney.imageAlt")}
                className="block w-full h-auto"
                data-testid="share-journey-image"
              />
            </div>
            <p className="mt-3 text-xs text-[#8A7D82] text-center leading-relaxed">
              {t("shareJourney.helper")}
            </p>
          </div>
        )}

        {state === "missing" && (
          <div
            className="py-8 text-center"
            data-testid="share-journey-missing"
          >
            <div className="h-12 w-12 rounded-full bg-[#FCE7E6] flex items-center justify-center mx-auto mb-3">
              <AlertCircle className="h-5 w-5 text-[#9B6A6C]" />
            </div>
            <h3 className="font-serif italic text-xl text-[#2C2123] mb-2">
              {t("shareJourney.missingTitle")}
            </h3>
            <p className="text-sm text-[#5C4B51] leading-relaxed mb-1">
              {t("shareJourney.missingBody", {
                count: missingSteps.length,
                steps: missingSteps.map((i) => i + 1).join(", "),
              })}
            </p>
            <p className="text-xs text-[#8A7D82] mt-3">
              {t("shareJourney.missingHelper")}
            </p>
          </div>
        )}

        {state === "error" && (
          <div
            className="py-8 text-center"
            data-testid="share-journey-error"
          >
            <div className="h-12 w-12 rounded-full bg-[#FCE7E6] flex items-center justify-center mx-auto mb-3">
              <AlertCircle className="h-5 w-5 text-[#9B6A6C]" />
            </div>
            <h3 className="font-serif italic text-xl text-[#2C2123] mb-2">
              {t("shareJourney.errorTitle")}
            </h3>
            <p className="text-sm text-[#5C4B51] leading-relaxed mb-4">
              {errorMsg || t("shareJourney.errorBody")}
            </p>
            <button
              type="button"
              onClick={compose}
              className="inline-flex items-center gap-2 text-sm text-[#9B6A6C] hover:text-[#7d5455] underline underline-offset-2"
              data-testid="share-journey-retry"
            >
              {t("shareJourney.retry")}
            </button>
          </div>
        )}
      </div>

      {/* Footer actions — only on ready */}
      {state === "ready" && (
        <div className="px-6 py-4 border-t border-[#EFE4E1] bg-white/40 flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={handleDownload}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-5 py-3 text-sm font-medium transition-all shadow-[0_4px_14px_rgba(155,106,108,0.32)] hover:-translate-y-0.5"
            data-testid="share-journey-download"
          >
            <Download className="h-4 w-4" />
            {t("shareJourney.download")}
          </button>
          {typeof navigator !== "undefined" &&
            typeof navigator.share === "function" && (
              <button
                type="button"
                onClick={handleNativeShare}
                className="flex-1 inline-flex items-center justify-center gap-2 border border-[#D5A8A4] text-[#5D4050] hover:bg-[#F8EEEE] rounded-full px-5 py-3 text-sm font-medium transition-all"
                data-testid="share-journey-native-share"
              >
                <Share2 className="h-4 w-4" />
                {t("shareJourney.share")}
              </button>
            )}
        </div>
      )}
    </motion.div>
  );
};

const ShareJourneyCard = ({ open, onClose, routineId, routineGoal }) => {
  const { t } = useTranslation();
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/55 backdrop-blur-sm p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="share-journey-overlay"
        >
          <Inner
            onClose={onClose}
            routineId={routineId}
            routineGoal={routineGoal}
            t={t}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ShareJourneyCard;
